import { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook to fetch and manage visibility settings
 * @returns {Object} { visibility, isPageVisible, isSectionVisible, getPageHiddenMessage, loading }
 */
export const useVisibility = () => {
  const [visibility, setVisibility] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVisibility = async () => {
      try {
        const response = await fetch(`${API_URL}/api/content/visibility`);
        if (response.ok) {
          const data = await response.json();
          setVisibility(data);
        }
      } catch (error) {
        console.error('Failed to fetch visibility settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchVisibility();
  }, []);

  /**
   * Check if a page is visible
   * @param {string} pageKey - The page key (home, program, speakers, sponsorship, golf, exhibit)
   * @returns {boolean}
   */
  const isPageVisible = (pageKey) => {
    if (!visibility || !visibility.pages) return true;
    return visibility.pages[pageKey]?.visible !== false;
  };

  /**
   * Check if a section is visible
   * @param {string} pageKey - The page key
   * @param {string} sectionKey - The section key within that page
   * @returns {boolean}
   */
  const isSectionVisible = (pageKey, sectionKey) => {
    if (!visibility || !visibility.sections) return true;
    const pageSections = visibility.sections[pageKey];
    if (!pageSections) return true;
    return pageSections[sectionKey]?.visible !== false;
  };

  /**
   * Check a navigation option
   * @param {string} optionKey - The navigation option key
   * @returns {boolean}
   */
  const isNavOptionEnabled = (optionKey) => {
    if (!visibility || !visibility.navigation) return true;
    return visibility.navigation[optionKey] !== false;
  };

  /**
   * Get the hidden message for a page
   * @param {string} pageKey - The page key
   * @returns {Object} { header, subtitle, body }
   */
  const getPageHiddenMessage = (pageKey) => {
    if (!visibility || !visibility.pages || !visibility.pages[pageKey]) {
      return {
        header: "Coming Soon",
        subtitle: "This page is currently under construction",
        body: "We're working on something exciting. Please check back soon."
      };
    }
    return visibility.pages[pageKey].hidden_message || {
      header: "Coming Soon",
      subtitle: "This page is currently under construction",
      body: "We're working on something exciting. Please check back soon."
    };
  };

  return {
    visibility,
    loading,
    isPageVisible,
    isSectionVisible,
    isNavOptionEnabled,
    getPageHiddenMessage
  };
};

export default useVisibility;
